import { Box, CircularProgress, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import OrgDetail from "@/components/usersOrgs/OrgDetail";
import { useOrganization } from "@/hooks/useOrganizations";
import { usePermissions } from "@/hooks/usePermissions";
import type { AccountDeepLink } from "@/interfaces/accountNav";

/**
 * Router state is untyped on the wire, so keep only the fields with the expected primitive
 * type and drop the message entirely when none survive. The tab NAMES are validated further
 * down (against the tabs the caller can see); this is the shape check.
 */
function readDeepLink(state: unknown): AccountDeepLink | null {
  if (typeof state !== "object" || state === null) return null;
  const { nonce, tab, groupId, groupTab } = state as Record<string, unknown>;
  const link: AccountDeepLink = {
    ...(typeof nonce === "number" ? { nonce } : {}),
    ...(typeof tab === "string" ? { tab } : {}),
    ...(typeof groupId === "number" ? { groupId } : {}),
    ...(typeof groupTab === "string" ? { groupTab } : {}),
  };
  return Object.keys(link).length > 0 ? link : null;
}

/**
 * Account — the signed-in user's own organization, rendered through the shared OrgDetail.
 * OrgDetail brings its own tabs (Details / Users / Groups / Plan), so this page adds none
 * of its own; a second tab row would just stack on top of them.
 *
 * Wallets, Transactions and Payment Methods are deliberately NOT here. A wallet belongs to
 * a GROUP (one Stripe Customer per group — groups.stripe_customer_id), so those live in
 * GroupView, reached via the Groups tab → View. An org-level Payment Methods tab would have
 * no single Stripe Customer to talk to.
 *
 * The exception is an organization whose group tree is hidden: OrgDetail then raises the root
 * group's Wallet, Payment Methods and Invoices to org-level tabs (components/usersOrgs/OrgDetail.tsx
 * ROOT_GROUP_TABS).
 *
 * The org id comes from the caller's resolved permissions and the fetch is scoped
 * server-side, so a Client Admin only ever loads their own org.
 *
 * Deep links land here with their target in the router state — the plan modal CTA
 * (`{ tab: "plan", nonce }`) and the upgrade drawer's payment-method link
 * (`{ tab: "groups", groupId, groupTab, nonce }`), and the Add Recipients wizard's Wallets link
 * (`{ tab: "payment-methods", nonce }`). This page is the only reader of that state:
 * it validates the shape once and hands the whole message down to OrgDetail, which routes the
 * group half on to GroupTree/GroupView. One reader means no component can consume or clear the
 * message out from under another.
 */
export default function Account() {
  const { organizationId, isLoaded, isGfg } = usePermissions();
  const { data: org, isLoading } = useOrganization(organizationId);
  const { state } = useLocation();
  const deepLink = readDeepLink(state);

  if (!isLoaded || isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress aria-label="Loading account" />
      </Box>
    );
  }

  if (!org) {
    return (
      <Typography sx={{ p: 3 }} color="text.secondary">
        We couldn't load your organization.
      </Typography>
    );
  }

  // No onBack — the Account view has no blotter to return to, so OrgDetail hides its
  // "← Organizations" back link here (still shown when reached via Users & Orgs).
  //
  // GFG Admins viewing their own org here get Users-only — mirrors the reference demo's
  // csTenant.internal handling (GfG's own tenant has no Groups/Wallet/Plan/etc., since it
  // doesn't run gifting through a client wallet). Client Admins keep every tab as-is. A
  // deep link to a tab that restrictToUsersTab hides (e.g. "plan") safely no-ops in
  // OrgDetail, since it's resolved against the caller's own visibleTabs.
  return <OrgDetail org={org} restrictToUsersTab={isGfg} deepLink={deepLink} />;
}
